import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Building, Plus, Pencil, Trash2, Users as UsersIcon, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../components/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function InternalAreas() {
  const { user } = useOutletContext();
  const [areas, setAreas] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '',
    source_warehouse_ids: [], destination_warehouse_id: '',
    member_user_ids: [], is_active: true, allow_rem: true,
  });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [a, w, u] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/internal-areas`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/warehouses`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setAreas(a.data || []);
      setWarehouses(w.data || []);
      setUsers(u.data || []);
    } catch (e) {
      toast.error('Error al cargar áreas');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', description: '', source_warehouse_ids: [], destination_warehouse_id: '', member_user_ids: [], is_active: true, allow_rem: true });
    setDialogOpen(true);
  };

  const openEdit = (area) => {
    setEditing(area);
    const srcs = (area.source_warehouse_ids && area.source_warehouse_ids.length)
      ? area.source_warehouse_ids
      : (area.source_warehouse_id ? [area.source_warehouse_id] : []);
    setForm({
      name: area.name,
      description: area.description || '',
      source_warehouse_ids: srcs,
      destination_warehouse_id: area.destination_warehouse_id || '',
      member_user_ids: area.member_user_ids || [],
      is_active: area.is_active,
      allow_rem: area.allow_rem !== false,
    });
    setDialogOpen(true);
  };

  const toggleSource = (whId) => {
    const has = form.source_warehouse_ids.includes(whId);
    setForm({
      ...form,
      source_warehouse_ids: has ? form.source_warehouse_ids.filter(id => id !== whId) : [...form.source_warehouse_ids, whId],
    });
  };

  const toggleMember = (userId) => {
    const has = form.member_user_ids.includes(userId);
    setForm({
      ...form,
      member_user_ids: has ? form.member_user_ids.filter(id => id !== userId) : [...form.member_user_ids, userId],
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.source_warehouse_ids.length === 0) {
      toast.error('Seleccioná al menos un depósito origen');
      return;
    }
    if (form.destination_warehouse_id && form.source_warehouse_ids.includes(form.destination_warehouse_id)) {
      toast.error('El depósito destino no puede ser también un depósito origen');
      return;
    }
    try {
      const payload = { ...form };
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/internal-areas/${editing.area_id}`, payload, { withCredentials: true });
        toast.success('Área actualizada');
      } else {
        await axios.post(`${BACKEND_URL}/api/internal-areas`, payload, { withCredentials: true });
        toast.success('Área creada');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const remove = async (area) => {
    if (!window.confirm(`¿Eliminar área "${area.name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/internal-areas/${area.area_id}`, { withCredentials: true });
      toast.success('Área eliminada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const isComplete = (area) => {
    const srcs = (area.source_warehouse_ids && area.source_warehouse_ids.length) ? area.source_warehouse_ids : (area.source_warehouse_id ? [area.source_warehouse_id] : []);
    return srcs.length > 0 && area.destination_warehouse_id && (area.member_user_ids?.length > 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Building className="w-7 h-7" /> Configuración Áreas Internas
        </h1>
        {isAdmin && (
          <Button onClick={openNew} data-testid="new-area-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva Área
          </Button>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle>Catálogo</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p>Cargando...</p> : areas.length === 0 ? <p className="text-muted-foreground">Sin áreas creadas.</p> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead>Destino</TableHead>
                  <TableHead>Miembros</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {areas.map(a => {
                  const srcIds = (a.source_warehouse_ids && a.source_warehouse_ids.length) ? a.source_warehouse_ids : (a.source_warehouse_id ? [a.source_warehouse_id] : []);
                  const srcNames = srcIds.map(id => warehouses.find(w => w.warehouse_id === id)?.name).filter(Boolean);
                  const dst = warehouses.find(w => w.warehouse_id === a.destination_warehouse_id);
                  const complete = isComplete(a);
                  return (
                    <TableRow key={a.area_id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {a.name}
                          {!complete && <AlertTriangle className="w-4 h-4 text-amber-500" title="Configuración incompleta" />}
                        </div>
                        {a.description && <div className="text-xs text-muted-foreground">{a.description}</div>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {srcNames.length === 0 ? <span className="text-red-500">— sin definir —</span> : (
                          <div className="flex flex-wrap gap-1">
                            {srcNames.map((n, i) => <Badge key={i} variant="outline" className="text-[11px]">{n}</Badge>)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{dst?.name || <span className="text-red-500">— sin definir —</span>}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1">
                          <UsersIcon className="w-3 h-3" /> {(a.member_user_ids || []).length}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 items-start">
                          <Badge className={a.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-200'}>
                            {a.is_active ? 'Activa' : 'Inactiva'}
                          </Badge>
                          <Badge variant="outline" className={a.allow_rem !== false ? 'border-teal-300 text-teal-700' : 'border-amber-300 text-amber-700'} data-testid={`area-rem-badge-${a.area_id}`}>
                            REM {a.allow_rem !== false ? 'ON' : 'OFF'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {isAdmin && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`edit-area-${a.area_id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(a)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Área' : 'Nueva Área'}</DialogTitle>
            <DialogDescription>Configura los depósitos origen/destino y los usuarios miembros.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="area-name" />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Depósitos Origen *</Label>
                <div className="border rounded-md p-2 space-y-1 max-h-40 overflow-y-auto">
                  {warehouses.filter(w => w.warehouse_id !== form.destination_warehouse_id).map(w => (
                    <label key={w.warehouse_id} className="flex items-center gap-2 cursor-pointer hover:bg-accent rounded px-2 py-1">
                      <input
                        type="checkbox"
                        checked={form.source_warehouse_ids.includes(w.warehouse_id)}
                        onChange={() => toggleSource(w.warehouse_id)}
                        data-testid={`area-source-${w.warehouse_id}`}
                      />
                      <span className="text-sm">{w.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">De dónde se solicita el stock. Podés marcar <strong>uno o varios</strong> depósitos centrales.</p>
              </div>
              <div>
                <Label>Depósito Destino *</Label>
                <select
                  value={form.destination_warehouse_id}
                  onChange={(e) => setForm({ ...form, destination_warehouse_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="area-destination"
                  required
                >
                  <option value="">— Sin asignar —</option>
                  {warehouses.filter(w => !form.source_warehouse_ids.includes(w.warehouse_id)).map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>)}
                </select>
                <p className="text-xs text-muted-foreground mt-1">Stock propio del área.</p>
              </div>
            </div>

            <div>
              <Label>Miembros del área</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Marcá los usuarios que pueden actuar sobre esta área. Sus permisos individuales (crear/aprobar/consumir) siguen definiéndose en el panel de Usuarios.
              </p>
              <div className="border rounded-md max-h-48 overflow-y-auto p-2 space-y-1">
                {users.length === 0 ? <p className="text-sm text-muted-foreground p-2">Sin usuarios disponibles.</p> :
                  users.map(u => (
                    <label key={u.user_id} className="flex items-center gap-2 cursor-pointer hover:bg-accent rounded px-2 py-1">
                      <input
                        type="checkbox"
                        checked={form.member_user_ids.includes(u.user_id)}
                        onChange={() => toggleMember(u.user_id)}
                        data-testid={`member-${u.user_id}`}
                      />
                      <span className="text-sm">
                        {u.name}{' '}
                        <span className="text-xs text-muted-foreground">({u.email}) — {u.role}</span>
                      </span>
                    </label>
                  ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="is_active"
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              <Label htmlFor="is_active" className="cursor-pointer">Activa</Label>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label htmlFor="allow_rem" className="cursor-pointer">Autorizar Ingresos por REM (Familiares)</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Si está apagado, esta área NO podrá recibir medicación traída por familiares (REM). No aparecerá en el selector de Ingresos de Familiares.
                </p>
              </div>
              <Switch
                id="allow_rem"
                checked={form.allow_rem}
                onCheckedChange={(v) => setForm({ ...form, allow_rem: v })}
                data-testid="area-allow-rem"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">{editing ? 'Guardar' : 'Crear'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title="Eliminar área"
        description={<>¿Eliminar el área <strong>{confirmDelete?.name}</strong>? Si tiene OPIs asociadas, el backend bloqueará la operación.</>}
        loading={deleting}
        onConfirm={remove}
        testId="confirm-delete-area"
      />
    </div>
  );
}

export default InternalAreas;
